/*
 * Parfum de Moi: fragrance builder
 * Reads its catalog (bases, families, notes) and the Shopify product from the
 * JSON config rendered by sections/fragrance-builder.liquid.
 */
(() => {
  'use strict';

  const ROMAN = ['I', 'II', 'III'];
  const STEPS = [
    { id: 'base', label: 'Base' },
    { id: 'notes', label: 'Notes' },
    { id: 'review', label: 'Review' }
  ];

  const esc = (value) =>
    String(value == null ? '' : value).replace(/[&<>"']/g, (c) => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
    }[c]));

  const reducedMotion = () =>
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const DEFAULT_STRINGS = {
    stepsLabel: 'Fragrance builder steps',
    baseHeading: 'Choose your base',
    baseHelp: 'Every fragrance begins with a base. Choose one, or choose two for an even 50/50 blend.',
    baseCounter0: 'No base chosen yet',
    baseCounter1: 'Single base · add another to blend 50/50',
    baseCounter2: '50/50 blend · the most a fragrance can hold',
    notesHeading: 'Add your notes',
    notesHelp: 'Add up to three notes from any scent families, or none at all. Switch families freely; your choices stay with you.',
    notesOptional: 'Notes are optional',
    familiesLabel: 'Scent families',
    reviewHeading: 'Your fragrance',
    summaryHeading: 'Your composition',
    viewComposition: 'View composition',
    select: 'Select',
    selected: 'Selected',
    blendWith: 'Blend 50/50',
    add: 'Add',
    added: 'Added',
    readMore: 'Read more',
    details: 'Details',
    close: 'Close',
    edit: 'Edit',
    editBase: 'Change base',
    editNotes: 'Change notes',
    addNotes: 'Add notes',
    baseLabel: 'Base',
    blendLabel: 'Base blend · 50/50',
    notesLabel: 'Add-in notes',
    sizeLabel: 'Size',
    priceLabel: 'Price',
    noBaseYet: 'Choose a base to begin',
    noNotesYet: 'None yet',
    noNotesReview: 'No add-in notes. Your base, just as it is.',
    noNotesValue: 'None',
    nextToNotes: 'Continue to notes',
    nextToReview: 'Review fragrance',
    nextToReviewNoNotes: 'Continue without notes',
    replaceBaseText: 'Your blend already holds two bases. Which one should make way for',
    replaceNoteText: 'You’ve chosen three notes. Which one should make way for',
    keepCurrent: 'Keep my current choices',
    needBase: 'Choose a base first.',
    chooseBase: 'Choose a base',
    addToCart: 'Add to cart',
    checkout: 'Checkout',
    adding: 'Adding your fragrance to the cart…',
    preparingCheckout: 'Adding your fragrance and preparing checkout…',
    toCheckout: 'Taking you to checkout…',
    addedConfirm: 'Your fragrance is in the cart',
    viewCart: 'View cart',
    soldOut: 'This fragrance is currently unavailable.',
    notConfigured: 'Ordering isn’t available right now.',
    genericError: 'We couldn’t add your fragrance to the cart.',
    networkError: 'We couldn’t reach the store. Check your connection.',
    tryAgain: 'Your selections are saved, so you can try again.',
    pending: '[Description pending]',
    pendingDetail: '[Detailed description pending]',
    heroPlaceholder: 'Image placeholder · Atelier',
    bottlePlaceholder: 'Image placeholder · 10 ml bottle',
    bottleAlt: 'Your 10 ml Parfum de Moi bottle',
    nameLabel: 'Name your fragrance',
    nameOptional: 'Optional',
    nameHelp: 'It appears with your order.',
    namePlaceholder: 'Your fragrance’s name',
    nameInvalid: 'Letters, numbers, spaces, and simple punctuation only.',
    suggest: 'Suggest names',
    suggestMore: 'More ideas',
    suggestionsLabel: 'Name ideas from your composition',
    nameSummary: 'Name'
  };

  class FragranceBuilder {
    /** Turns the flat block list from Liquid into bases and families (notes follow their family). */
    static normalize(raw) {
      const config = { ...raw };
      config.strings = { ...DEFAULT_STRINGS, ...(raw.strings || {}) };
      if (Array.isArray(raw.blocks)) {
        const bases = [];
        const families = [];
        let current = null;
        const seen = new Set();
        const uniqueId = (id) => {
          let out = id || 'item';
          let n = 2;
          while (seen.has(out)) out = `${id}-${n++}`;
          seen.add(out);
          return out;
        };
        raw.blocks.forEach((block) => {
          if (!block.name) return;
          if (block.type === 'base') {
            if (block.visible) bases.push({ ...block, id: uniqueId(`base-${block.id}`) });
          } else if (block.type === 'family') {
            current = { ...block, id: uniqueId(`family-${block.id}`), notes: [] };
            if (block.visible) families.push(current);
          } else if (block.type === 'note') {
            if (!current || !block.visible || !current.visible) return;
            current.notes.push({ ...block, id: uniqueId(`note-${block.id}`) });
          }
        });
        config.bases = bases;
        config.families = families.filter((f) => f.notes.length);
      }
      config.bases = config.bases || [];
      config.families = config.families || [];
      return config;
    }

    constructor(root) {
      this.root = root;
      const configEl = root.querySelector('script[data-fb-config]');
      this.config = FragranceBuilder.normalize(JSON.parse(configEl.textContent));
      this.limits = { bases: 2, notes: 3 };
      this.busy = false;
      this.replaceRequest = null;
      this.lastTrigger = null;

      this.baseMap = new Map(this.config.bases.map((b) => [b.id, b]));
      this.noteMap = new Map();
      this.config.families.forEach((family) => {
        family.notes.forEach((note) => this.noteMap.set(note.id, { ...note, familyId: family.id, familyName: family.name }));
      });

      this.storageKey = `pdm-fb:${this.config.sectionId}`;
      this.state = this.restore();

      this.render();
      this.bind();
      this.update({ focus: false });
    }

    /* ---------- state ---------- */

    restore() {
      const fallback = {
        step: 'base',
        bases: [],
        notes: [],
        family: this.config.families[0] ? this.config.families[0].id : null,
        name: '',
        lastAdded: null
      };
      try {
        const saved = JSON.parse(window.sessionStorage.getItem(this.storageKey) || 'null');
        if (!saved) return fallback;
        // Drop anything no longer offered (e.g. after a seasonal note change).
        const bases = (saved.bases || []).filter((id) => this.baseMap.has(id)).slice(0, this.limits.bases);
        const notes = (saved.notes || []).filter((id) => this.noteMap.has(id)).slice(0, this.limits.notes);
        const family = this.config.families.some((f) => f.id === saved.family) ? saved.family : fallback.family;
        const step = STEPS.some((s) => s.id === saved.step) && bases.length ? saved.step : 'base';
        const name = this.cleanName(saved.name || '').value;
        return { step, bases, notes, family, name, lastAdded: saved.lastAdded || null };
      } catch (e) {
        return fallback;
      }
    }

    persist() {
      try {
        window.sessionStorage.setItem(this.storageKey, JSON.stringify(this.state));
      } catch (e) { /* storage unavailable: selections simply won't survive a reload */ }
    }

    orderedBases() {
      return this.config.bases.filter((b) => this.state.bases.includes(b.id));
    }

    orderedNotes() {
      const order = [];
      this.config.families.forEach((f) => f.notes.forEach((n) => order.push(n.id)));
      return order.filter((id) => this.state.notes.includes(id)).map((id) => this.noteMap.get(id));
    }

    baseLabel() {
      const bases = this.orderedBases();
      if (!bases.length) return '';
      if (bases.length === 1) return bases[0].name;
      return `${bases[0].name} + ${bases[1].name} (50/50 blend)`;
    }

    notesLabel() {
      const notes = this.orderedNotes();
      return notes.length ? notes.map((n) => n.name).join(', ') : this.config.strings.noNotesValue;
    }

    signature() {
      return [this.config.product.variantId, this.orderedBases().map((b) => b.id).join('+'),
        this.orderedNotes().map((n) => n.id).join('+'), this.finalName().toLowerCase()].join('|');
    }

    lineItemProperties() {
      const props = {};
      const name = this.finalName();
      if (name) props['Fragrance name'] = name;
      if (this.config.product.includeSizeProperty) props.Size = this.config.product.sizeLabel;
      props.Base = this.baseLabel();
      props['Add-in notes'] = this.notesLabel();
      props._config = this.signature();
      return props;
    }

    /* ---------- naming ---------- */

    namingEnabled() {
      return !this.config.naming || this.config.naming.enabled !== false;
    }

    nameMax() {
      return (this.config.naming && Number(this.config.naming.maxLength)) || 30;
    }

    /** Keeps letters (any language), digits, spaces and simple punctuation. */
    cleanName(raw) {
      const value = String(raw || '')
        .replace(/[^\p{L}\p{M}\p{N} '’&.,!?-]/gu, '')
        .replace(/\s{2,}/g, ' ')
        .slice(0, this.nameMax());
      return { value, changed: value !== String(raw || '').slice(0, this.nameMax()) };
    }

    finalName() {
      return this.namingEnabled() ? (this.state.name || '').trim() : '';
    }

    suggestNames(count = 4) {
      const split = (text) => String(text || '').split(',').map((w) => w.trim()).filter(Boolean);
      const bases = this.orderedBases();
      const notes = this.orderedNotes();
      const adjectives = bases.flatMap((b) => split(b.words));
      if (!adjectives.length) bases.forEach((b) => adjectives.push(b.name.split(' ')[0]));
      const perNote = notes.map((n) => (split(n.words).length ? split(n.words) : [n.name]));
      const nouns = perNote.flat();
      const generic = ['Reverie', 'Hour', 'Portrait', 'Memory', 'Letters', 'Room'];
      const pick = (list) => list[Math.floor(Math.random() * list.length)];
      const subject = () => (nouns.length ? pick(nouns) : pick(adjectives));

      const patterns = [
        () => `${pick(adjectives)} ${nouns.length ? pick(nouns) : pick(generic)}`,
        () => (perNote.length > 1 ? (() => {
          const [a, b] = [...perNote].sort(() => Math.random() - 0.5);
          return `${pick(a)} & ${pick(b)}`;
        })() : null),
        () => `The ${subject()} Hour`,
        () => `${subject()} de Moi`,
        () => `Portrait in ${subject()}`,
        () => `${pick(adjectives)} ${pick(generic)}`
      ];
      const out = new Set();
      const current = (this.state.name || '').trim().toLowerCase();
      for (let i = 0; i < 80 && out.size < count; i += 1) {
        const idea = patterns[i % patterns.length]();
        if (!idea || idea.length > this.nameMax()) continue;
        if (idea.toLowerCase() === current) continue;
        if ([...out].some((x) => x.toLowerCase() === idea.toLowerCase())) continue;
        out.add(idea);
      }
      return [...out];
    }

    namingHTML() {
      const s = this.config.strings;
      const id = `${this.config.sectionId}-name`;
      return `
        <div class="pdm-fb__naming">
          <label for="${id}" class="pdm-fb__label">${esc(s.nameLabel)} <span class="pdm-fb__muted">${esc(s.nameOptional)}</span></label>
          <div class="pdm-fb__name-field">
            <input id="${id}" class="pdm-fb__name-input" type="text" data-name-input maxlength="${this.nameMax()}"
              autocomplete="off" spellcheck="true" placeholder="${esc(s.namePlaceholder)}" aria-describedby="${id}-help">
            <span class="pdm-fb__name-count" data-name-count aria-hidden="true"></span>
          </div>
          <p id="${id}-help" class="pdm-fb__name-help" data-name-help>${esc(s.nameHelp)} Up to ${this.nameMax()} characters.</p>
          <button type="button" class="pdm-fb__link pdm-fb__suggest" data-suggest>${esc(s.suggest)}</button>
          <ul class="pdm-fb__suggestions" data-suggestions role="list" aria-label="${esc(s.suggestionsLabel)}"></ul>
        </div>`;
    }

    syncName() {
      const input = this.q('[data-name-input]');
      if (!input) return;
      if (document.activeElement !== input && input.value !== this.state.name) input.value = this.state.name;
      const count = this.q('[data-name-count]');
      if (count) count.textContent = `${input.value.length}/${this.nameMax()}`;
    }

    showSuggestions() {
      const s = this.config.strings;
      const ideas = this.suggestNames();
      this.q('[data-suggestions]').innerHTML = ideas.map((idea) => `
        <li><button type="button" class="pdm-fb__idea" data-use-name="${esc(idea)}">${esc(idea)}</button></li>`).join('');
      this.q('[data-suggest]').textContent = s.suggestMore;
      this.announce(`Name ideas: ${ideas.join(', ')}.`);
    }

    /* ---------- rendering ---------- */

    placeholder(label, tone, image, alt) {
      if (image) {
        return `<div class="pdm-fb__media"><img src="${esc(image)}" alt="${esc(alt || '')}" loading="lazy"></div>`;
      }
      return `<div class="pdm-fb__media pdm-fb__media--placeholder" style="--tone:${esc(tone || '#EDE9E3')}" role="img" aria-label="${esc(label)}">
        <span class="pdm-fb__placeholder-label" aria-hidden="true">${esc(label)}</span>
      </div>`;
    }

    render() {
      const c = this.config;
      const s = c.strings;
      this.root.innerHTML = `
        <div class="pdm-fb">
          <header class="pdm-fb__intro">
            <div class="pdm-fb__intro-text">
              ${c.eyebrow ? `<p class="pdm-fb__eyebrow">${esc(c.eyebrow)}</p>` : ''}
              <h1 class="pdm-fb__title">${esc(c.heading)}</h1>
              ${c.intro ? `<p class="pdm-fb__lede">${esc(c.intro)}</p>` : ''}
            </div>
            ${this.placeholder(s.heroPlaceholder, '#E8E2DA', c.heroImage, c.heading)}
          </header>

          <nav class="pdm-fb__steps" aria-label="${esc(s.stepsLabel)}">
            <ol>
              ${STEPS.map((step, i) => `
                <li><button type="button" class="pdm-fb__step" data-goto="${step.id}">
                  <span class="pdm-fb__step-num" aria-hidden="true">${ROMAN[i]}</span>
                  <span class="pdm-fb__step-label"><span class="visually-hidden">Step ${i + 1}: </span>${esc(step.label)}</span>
                </button></li>`).join('')}
            </ol>
          </nav>

          <div class="pdm-fb__layout">
            <div class="pdm-fb__stage">
              <section class="pdm-fb__panel" data-panel="base" aria-labelledby="${c.sectionId}-h-base">
                <div class="pdm-fb__panel-head">
                  <p class="pdm-fb__kicker">I</p>
                  <h2 id="${c.sectionId}-h-base" class="pdm-fb__h2" tabindex="-1">${esc(s.baseHeading)}</h2>
                  <p class="pdm-fb__help">${esc(s.baseHelp)}</p>
                  <p class="pdm-fb__counter" data-base-counter></p>
                </div>
                <div class="pdm-fb__replace" data-replace="base" hidden></div>
                <ul class="pdm-fb__grid pdm-fb__grid--bases" role="list">
                  ${c.bases.map((b) => `
                    <li class="pdm-fb__card pdm-fb__card--base" data-base-card="${esc(b.id)}">
                      ${this.placeholder(`Image placeholder · ${b.name}`, b.tone, b.image, b.name)}
                      <div class="pdm-fb__card-body">
                        <h3 class="pdm-fb__card-title">${esc(b.name)}</h3>
                        <p class="pdm-fb__smells"><span class="pdm-fb__smells-label">Smells like</span> ${esc(b.short || s.pending)}</p>
                        <div class="pdm-fb__card-actions">
                          <button type="button" class="pdm-fb__select" data-toggle-base="${esc(b.id)}" aria-pressed="false">
                            <span class="pdm-fb__select-text">${esc(s.select)}</span>
                          </button>
                          <button type="button" class="pdm-fb__link" data-detail-base="${esc(b.id)}" aria-haspopup="dialog">
                            ${esc(s.readMore)}<span class="visually-hidden"> about ${esc(b.name)}</span>
                          </button>
                        </div>
                      </div>
                    </li>`).join('')}
                </ul>
              </section>

              <section class="pdm-fb__panel" data-panel="notes" aria-labelledby="${c.sectionId}-h-notes" hidden>
                <div class="pdm-fb__panel-head">
                  <p class="pdm-fb__kicker">II${c.seasonLabel ? ` · ${esc(c.seasonLabel)}` : ''}</p>
                  <h2 id="${c.sectionId}-h-notes" class="pdm-fb__h2" tabindex="-1">${esc(s.notesHeading)}</h2>
                  <p class="pdm-fb__help">${esc(s.notesHelp)}</p>
                  <p class="pdm-fb__counter" data-note-counter></p>
                  <ul class="pdm-fb__chosen" data-note-chips role="list"></ul>
                </div>
                <div class="pdm-fb__families" role="group" aria-label="${esc(s.familiesLabel)}">
                  ${c.families.map((f) => `
                    <button type="button" class="pdm-fb__family" data-family="${esc(f.id)}" aria-pressed="false" aria-controls="${c.sectionId}-family-panel">
                      <span class="pdm-fb__family-swatch" style="--tone:${esc(f.tone)}" aria-hidden="true"></span>
                      <span class="pdm-fb__family-name">${esc(f.name)}</span>
                      <span class="pdm-fb__family-count" data-family-count="${esc(f.id)}"></span>
                    </button>`).join('')}
                </div>
                <div class="pdm-fb__replace" data-replace="notes" hidden></div>
                <div id="${c.sectionId}-family-panel" class="pdm-fb__family-panel" data-family-panel aria-live="polite"></div>
              </section>

              <section class="pdm-fb__panel" data-panel="review" aria-labelledby="${c.sectionId}-h-review" hidden>
                <div class="pdm-fb__panel-head">
                  <p class="pdm-fb__kicker">III</p>
                  <h2 id="${c.sectionId}-h-review" class="pdm-fb__h2" tabindex="-1">${esc(s.reviewHeading)}</h2>
                </div>
                <div class="pdm-fb__review">
                  ${this.placeholder(s.bottlePlaceholder, '#ECE7E1', c.bottleImage, s.bottleAlt)}
                  <div class="pdm-fb__review-body">
                    ${this.namingEnabled() ? this.namingHTML() : ''}
                    <div data-review></div>
                  </div>
                </div>
              </section>
            </div>

            <aside class="pdm-fb__aside" aria-labelledby="${c.sectionId}-h-summary">
              <div class="pdm-fb__summary">
                <h2 id="${c.sectionId}-h-summary" class="pdm-fb__summary-title">${esc(s.summaryHeading)}</h2>
                <div data-summary></div>
                <button type="button" class="pdm-fb__button pdm-fb__button--primary" data-next></button>
              </div>
            </aside>
          </div>

          <div class="pdm-fb__bar" data-bar>
            <button type="button" class="pdm-fb__bar-summary" data-open-sheet aria-haspopup="dialog">
              <span class="pdm-fb__bar-line" data-bar-line></span>
              <span class="pdm-fb__bar-hint">${esc(s.viewComposition)}</span>
            </button>
            <button type="button" class="pdm-fb__button pdm-fb__button--primary pdm-fb__bar-next" data-next></button>
          </div>

          <dialog class="pdm-fb__dialog" data-detail-dialog aria-labelledby="${c.sectionId}-dlg-title">
            <div class="pdm-fb__dialog-inner">
              <button type="button" class="pdm-fb__close" data-close-dialog aria-label="${esc(s.close)}">
                <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true"><path d="M5 5l14 14M19 5L5 19" stroke="currentColor" stroke-width="1.2" fill="none"/></svg>
              </button>
              <div data-detail-body></div>
            </div>
          </dialog>

          <dialog class="pdm-fb__dialog pdm-fb__dialog--sheet" data-sheet aria-labelledby="${c.sectionId}-sheet-title">
            <div class="pdm-fb__dialog-inner">
              <button type="button" class="pdm-fb__close" data-close-dialog aria-label="${esc(s.close)}">
                <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true"><path d="M5 5l14 14M19 5L5 19" stroke="currentColor" stroke-width="1.2" fill="none"/></svg>
              </button>
              <h2 id="${c.sectionId}-sheet-title" class="pdm-fb__summary-title">${esc(s.summaryHeading)}</h2>
              <div data-sheet-summary></div>
            </div>
          </dialog>

          <p class="visually-hidden" data-live aria-live="polite" aria-atomic="true"></p>
        </div>`;

      this.q = (sel) => this.root.querySelector(sel);
      this.qa = (sel) => Array.from(this.root.querySelectorAll(sel));
      this.detailDialog = this.q('[data-detail-dialog]');
      this.sheet = this.q('[data-sheet]');
      this.live = this.q('[data-live]');
    }

    /* ---------- events ---------- */

    bind() {
      this.root.addEventListener('click', (event) => {
        const t = event.target.closest('button, a');
        if (!t || !this.root.contains(t)) return;
        const d = t.dataset;

        if (d.goto) return this.goTo(d.goto);
        if (d.next !== undefined) return this.next();
        if (d.toggleBase) return this.toggleBase(d.toggleBase, t);
        if (d.detailBase) return this.openDetail('base', d.detailBase, t);
        if (d.family) return this.setFamily(d.family);
        if (d.toggleNote) return this.toggleNote(d.toggleNote, t);
        if (d.detailNote) return this.openDetail('note', d.detailNote, t);
        if (d.removeNote) return this.removeNote(d.removeNote);
        if (d.removeBase) return this.removeBase(d.removeBase);
        if (d.replaceWith) return this.resolveReplace(d.replaceWith);
        if (d.replaceCancel !== undefined) return this.cancelReplace();
        if (d.dialogToggle) {
          const kind = d.dialogToggle;
          const id = d.id;
          this.closeDialog(this.detailDialog, false);
          const sel = kind === 'base' ? `[data-toggle-base="${id}"]` : `[data-toggle-note="${id}"]`;
          if (kind === 'base') this.toggleBase(id, this.q(sel));
          else this.toggleNote(id, this.q(sel));
          if (!this.replaceRequest) {
            const again = this.q(sel);
            if (again) again.focus();
          }
          return;
        }
        if (d.editStep) { this.closeDialog(this.sheet, false); return this.goTo(d.editStep); }
        if (d.closeDialog !== undefined) return this.closeDialog(t.closest('dialog'));
        if (d.openSheet !== undefined) return this.openSheet(t);
        if (d.addToCart !== undefined) return this.purchase({ checkout: false });
        if (d.checkout !== undefined) return this.purchase({ checkout: true });
        if (d.viewCart !== undefined) return this.viewCart(event);
        if (d.suggest !== undefined) return this.showSuggestions();
        if (d.useName) {
          this.state.name = this.cleanName(d.useName).value;
          this.syncName();
          this.update();
          const input = this.q('[data-name-input]');
          if (input) input.focus();
          this.announce(`Name set to ${this.state.name}.`);
          return;
        }
      });

      this.root.addEventListener('input', (event) => {
        const input = event.target.closest('[data-name-input]');
        if (!input) return;
        const { value, changed } = this.cleanName(input.value);
        if (changed) {
          const pos = Math.max(0, input.selectionStart - (input.value.length - value.length));
          input.value = value;
          input.setSelectionRange(pos, pos);
        }
        const help = this.q('[data-name-help]');
        help.classList.toggle('is-error', changed);
        help.textContent = changed ? this.config.strings.nameInvalid : `${this.config.strings.nameHelp} Up to ${this.nameMax()} characters.`;
        this.state.name = value;
        this.syncName();
        this.update();
      });

      window.addEventListener('pageshow', (event) => {
        if (event.persisted) this.unlock();
      });

      [this.detailDialog, this.sheet].forEach((dialog) => {
        // Click on the backdrop closes the dialog.
        dialog.addEventListener('click', (event) => {
          if (event.target === dialog) this.closeDialog(dialog);
        });
        dialog.addEventListener('close', () => {
          document.documentElement.classList.remove('pdm-fb-lock');
          if (!this.suppressRestore && this.lastTrigger && document.contains(this.lastTrigger)) this.lastTrigger.focus();
          this.suppressRestore = false;
        });
      });
    }

    announce(message) {
      this.live.textContent = '';
      window.setTimeout(() => { this.live.textContent = message; }, 30);
    }

    /* ---------- navigation ---------- */

    goTo(step, { focus = true } = {}) {
      if (step !== 'base' && !this.state.bases.length) {
        this.state.step = 'base';
        this.update({ focus: true });
        this.announce(this.config.strings.needBase);
        this.flash(this.q('[data-base-counter]'));
        return;
      }
      this.state.step = step;
      this.cancelReplace(false);
      this.update({ focus });
      const top = this.q('.pdm-fb__steps');
      if (top && top.getBoundingClientRect().top < 0) {
        top.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: 'start' });
      }
    }

    next() {
      const index = STEPS.findIndex((s) => s.id === this.state.step);
      if (index < STEPS.length - 1) this.goTo(STEPS[index + 1].id);
    }

    flash(el) {
      if (!el) return;
      el.classList.remove('is-flash');
      void el.offsetWidth;
      el.classList.add('is-flash');
    }

    /* ---------- bases ---------- */

    toggleBase(id, trigger) {
      const base = this.baseMap.get(id);
      if (!base) return;
      if (this.state.bases.includes(id)) {
        this.removeBase(id);
        return;
      }
      if (this.state.bases.length >= this.limits.bases) {
        this.askReplace('base', id, trigger);
        return;
      }
      this.state.bases.push(id);
      this.cancelReplace(false);
      this.update();
      this.announce(this.state.bases.length === 2
        ? `${base.name} added. Your fragrance is now a 50/50 blend of ${this.orderedBases().map((b) => b.name).join(' and ')}.`
        : `${base.name} selected as your base.`);
    }

    removeBase(id) {
      const base = this.baseMap.get(id);
      this.state.bases = this.state.bases.filter((b) => b !== id);
      this.update();
      this.announce(`${base ? base.name : 'Base'} removed.${this.state.bases.length ? '' : ' Choose a base to continue.'}`);
    }

    /* ---------- notes ---------- */

    setFamily(id) {
      this.state.family = id;
      this.cancelReplace(false);
      this.update({ focus: false });
      const family = this.config.families.find((f) => f.id === id);
      if (family) this.announce(`Showing ${family.name}: ${family.notes.length} notes.`);
    }

    toggleNote(id, trigger) {
      const note = this.noteMap.get(id);
      if (!note) return;
      if (this.state.notes.includes(id)) {
        this.removeNote(id);
        return;
      }
      if (this.state.notes.length >= this.limits.notes) {
        this.askReplace('notes', id, trigger);
        return;
      }
      this.state.notes.push(id);
      this.cancelReplace(false);
      this.update();
      this.announce(`${note.name} added. ${this.state.notes.length} of ${this.limits.notes} notes chosen.`);
    }

    removeNote(id) {
      const note = this.noteMap.get(id);
      this.state.notes = this.state.notes.filter((n) => n !== id);
      this.update();
      this.announce(`${note ? note.name : 'Note'} removed. ${this.state.notes.length} of ${this.limits.notes} notes chosen.`);
    }

    /* ---------- replace flow ---------- */

    askReplace(kind, incomingId, trigger) {
      const s = this.config.strings;
      const isBase = kind === 'base';
      const incoming = isBase ? this.baseMap.get(incomingId) : this.noteMap.get(incomingId);
      const current = isBase ? this.orderedBases() : this.orderedNotes();
      const selector = isBase ? `[data-toggle-base="${incomingId}"]` : `[data-toggle-note="${incomingId}"]`;
      this.replaceRequest = { kind, incomingId, trigger, selector };
      this.qa('[data-replace]').forEach((el) => { el.hidden = true; el.innerHTML = ''; });
      const box = this.q(`[data-replace="${kind}"]`);
      box.innerHTML = `
        <p class="pdm-fb__replace-text" tabindex="-1">${esc(isBase ? s.replaceBaseText : s.replaceNoteText)}
          <strong>${esc(incoming.name)}</strong>?</p>
        <div class="pdm-fb__replace-actions">
          ${current.map((item) => `<button type="button" class="pdm-fb__button pdm-fb__button--quiet" data-replace-with="${esc(item.id)}">Replace ${esc(item.name)}</button>`).join('')}
          <button type="button" class="pdm-fb__link" data-replace-cancel>${esc(s.keepCurrent)}</button>
        </div>`;
      box.hidden = false;
      box.querySelector('.pdm-fb__replace-text').focus();
      this.announce(isBase ? s.replaceBaseText + ' ' + incoming.name + '?' : s.replaceNoteText + ' ' + incoming.name + '?');
    }

    resolveReplace(outgoingId) {
      const req = this.replaceRequest;
      if (!req) return;
      const list = req.kind === 'base' ? this.state.bases : this.state.notes;
      const index = list.indexOf(outgoingId);
      if (index === -1) return;
      list.splice(index, 1, req.incomingId);
      const outgoing = req.kind === 'base' ? this.baseMap.get(outgoingId) : this.noteMap.get(outgoingId);
      const incoming = req.kind === 'base' ? this.baseMap.get(req.incomingId) : this.noteMap.get(req.incomingId);
      this.cancelReplace(false);
      this.update();
      this.announce(`${outgoing.name} replaced with ${incoming.name}.`);
      const again = this.q(req.selector);
      if (again) again.focus();
    }

    cancelReplace(restoreFocus = true) {
      const req = this.replaceRequest;
      this.replaceRequest = null;
      this.qa('[data-replace]').forEach((el) => { el.hidden = true; el.innerHTML = ''; });
      if (restoreFocus && req) {
        const again = this.q(req.selector);
        if (again) again.focus();
      }
    }

    /* ---------- dialogs ---------- */

    openDetail(kind, id, trigger) {
      const s = this.config.strings;
      const item = kind === 'base' ? this.baseMap.get(id) : this.noteMap.get(id);
      if (!item) return;
      const selected = kind === 'base' ? this.state.bases.includes(id) : this.state.notes.includes(id);
      const rows = kind === 'base'
        ? [['Smells like', item.short], ['Mood', item.mood], ['Texture', item.texture], ['Intensity', item.intensity], ['Role in a blend', item.role]]
        : [['Mood', item.mood], ['Texture', item.texture], ['Intensity', item.intensity], ['Role in a blend', item.role]];
      const body = kind === 'base' ? item.detail : item.smell;
      this.lastTrigger = trigger;
      this.q('[data-detail-body]').innerHTML = `
        <p class="pdm-fb__kicker">${esc(kind === 'base' ? 'Base' : item.familyName)}</p>
        <h2 id="${this.config.sectionId}-dlg-title" class="pdm-fb__dialog-title">${esc(item.name)}</h2>
        ${kind === 'note' ? `<p class="pdm-fb__dialog-short">${esc(item.short || s.pending)}</p>` : ''}
        <h3 class="pdm-fb__dialog-sub">${esc(kind === 'base' ? 'The base' : 'How it smells')}</h3>
        <p class="pdm-fb__dialog-text">${esc(body || s.pendingDetail)}</p>
        <dl class="pdm-fb__facts">
          ${rows.map(([k, v]) => `<div><dt>${esc(k)}</dt><dd>${esc(v || s.pending)}</dd></div>`).join('')}
        </dl>
        <button type="button" class="pdm-fb__button ${selected ? 'pdm-fb__button--quiet' : 'pdm-fb__button--primary'}" data-dialog-toggle="${kind}" data-id="${esc(id)}">
          ${esc(selected ? `Remove ${item.name}` : `Add ${item.name}`)}
        </button>`;
      this.showDialog(this.detailDialog);
    }

    openSheet(trigger) {
      this.lastTrigger = trigger;
      this.q('[data-sheet-summary]').innerHTML = this.summaryHTML(true);
      this.showDialog(this.sheet);
    }

    showDialog(dialog) {
      if (typeof dialog.showModal === 'function') {
        dialog.showModal();
      } else {
        dialog.setAttribute('open', '');
      }
      document.documentElement.classList.add('pdm-fb-lock');
      const focusTarget = dialog.querySelector('h2') || dialog.querySelector('button');
      if (focusTarget) {
        focusTarget.setAttribute('tabindex', '-1');
        focusTarget.focus();
      }
    }

    closeDialog(dialog, restore = true) {
      if (!dialog || !dialog.hasAttribute('open')) return;
      this.suppressRestore = !restore;
      if (typeof dialog.close === 'function') {
        dialog.close();
      } else {
        dialog.removeAttribute('open');
        dialog.dispatchEvent(new Event('close'));
      }
      document.documentElement.classList.remove('pdm-fb-lock');
    }

    /* ---------- summary & review ---------- */

    summaryHTML(inSheet = false) {
      const s = this.config.strings;
      const bases = this.orderedBases();
      const notes = this.orderedNotes();
      const p = this.config.product;
      const edit = (step, label) => `<button type="button" class="pdm-fb__link pdm-fb__edit" data-edit-step="${step}">${esc(s.edit)}<span class="visually-hidden"> ${esc(label)}</span></button>`;
      return `
        <dl class="pdm-fb__summary-list">
          ${this.finalName() ? `<div class="pdm-fb__summary-row"><dt>${esc(s.nameSummary)}</dt><dd><span class="pdm-fb__summary-item pdm-fb__summary-name">${esc(this.finalName())}</span></dd></div>` : ''}
          <div class="pdm-fb__summary-row">
            <dt>${esc(bases.length === 2 ? s.blendLabel : s.baseLabel)} ${inSheet || this.state.step !== 'base' ? edit('base', 'base') : ''}</dt>
            <dd>${bases.length ? bases.map((b) => `<span class="pdm-fb__summary-item">${esc(b.name)}${bases.length === 2 ? ' <span class="pdm-fb__muted">50%</span>' : ''}</span>`).join('') : `<span class="pdm-fb__muted">${esc(s.noBaseYet)}</span>`}</dd>
          </div>
          <div class="pdm-fb__summary-row">
            <dt>${esc(s.notesLabel)} <span class="pdm-fb__muted">${notes.length} of ${this.limits.notes}</span> ${inSheet || this.state.step !== 'notes' ? edit('notes', 'notes') : ''}</dt>
            <dd>${notes.length ? notes.map((n) => `<span class="pdm-fb__summary-item">${esc(n.name)}</span>`).join('') : `<span class="pdm-fb__muted">${esc(s.noNotesYet)}</span>`}</dd>
          </div>
          <div class="pdm-fb__summary-row pdm-fb__summary-row--inline">
            <dt>${esc(s.sizeLabel)}</dt><dd>${esc(p.sizeLabel)}</dd>
          </div>
          <div class="pdm-fb__summary-row pdm-fb__summary-row--inline">
            <dt>${esc(s.priceLabel)}</dt><dd>${esc(p.priceFormatted)}</dd>
          </div>
        </dl>`;
    }

    reviewHTML() {
      const s = this.config.strings;
      const p = this.config.product;
      const bases = this.orderedBases();
      const notes = this.orderedNotes();
      const unavailable = !p.variantId || !p.available;
      return `
        <dl class="pdm-fb__review-list">
          <div>
            <dt>${esc(bases.length === 2 ? s.blendLabel : s.baseLabel)}</dt>
            <dd>
              ${bases.length ? bases.map((b) => `<p class="pdm-fb__review-name">${esc(b.name)}${bases.length === 2 ? ' <span class="pdm-fb__muted">· 50%</span>' : ''}</p><p class="pdm-fb__review-desc">${esc(b.short)}</p>`).join('') : `<p class="pdm-fb__muted">${esc(s.noBaseYet)}</p>`}
              <button type="button" class="pdm-fb__link" data-goto="base">${esc(s.editBase)}</button>
            </dd>
          </div>
          <div>
            <dt>${esc(s.notesLabel)} <span class="pdm-fb__muted">${notes.length} of ${this.limits.notes}</span></dt>
            <dd>
              ${notes.length ? notes.map((n) => `<p class="pdm-fb__review-name">${esc(n.name)} <span class="pdm-fb__muted">· ${esc(n.familyName)}</span></p><p class="pdm-fb__review-desc">${esc(n.short)}</p>`).join('') : `<p class="pdm-fb__review-desc">${esc(s.noNotesReview)}</p>`}
              <button type="button" class="pdm-fb__link" data-goto="notes">${esc(notes.length ? s.editNotes : s.addNotes)}</button>
            </dd>
          </div>
          <div class="pdm-fb__review-inline"><dt>${esc(s.sizeLabel)}</dt><dd>${esc(p.sizeLabel)}</dd></div>
          <div class="pdm-fb__review-inline pdm-fb__review-price"><dt>${esc(s.priceLabel)}</dt><dd>${esc(p.priceFormatted)}</dd></div>
        </dl>
        <div class="pdm-fb__purchase">
          ${unavailable ? `<p class="pdm-fb__error" role="alert">${esc(p.variantId ? s.soldOut : s.notConfigured)}</p>` : ''}
          <div class="pdm-fb__purchase-actions">
            <button type="button" class="pdm-fb__button pdm-fb__button--secondary" data-add-to-cart ${unavailable ? 'aria-disabled="true"' : ''}>
              <span class="pdm-fb__button-text">${esc(s.addToCart)}</span>
            </button>
            <button type="button" class="pdm-fb__button pdm-fb__button--primary" data-checkout ${unavailable ? 'aria-disabled="true"' : ''}>
              <span class="pdm-fb__button-text">${esc(s.checkout)}</span>
            </button>
          </div>
          <div class="pdm-fb__status" data-status role="status" aria-live="polite"></div>
        </div>`;
    }

    familyPanelHTML() {
      const s = this.config.strings;
      const family = this.config.families.find((f) => f.id === this.state.family) || this.config.families[0];
      if (!family) return `<p class="pdm-fb__muted">${esc(s.pending)}</p>`;
      return `
        <div class="pdm-fb__family-intro">
          ${this.placeholder(`Image placeholder · ${family.name}`, family.tone, family.image, family.name)}
          <div>
            <h3 class="pdm-fb__family-title">${esc(family.name)}</h3>
            ${family.description ? `<p class="pdm-fb__help">${esc(family.description)}</p>` : ''}
          </div>
        </div>
        <ul class="pdm-fb__grid pdm-fb__grid--notes" role="list">
          ${family.notes.map((n) => {
            const on = this.state.notes.includes(n.id);
            return `
              <li class="pdm-fb__card pdm-fb__card--note ${on ? 'is-selected' : ''}">
                <div class="pdm-fb__card-body">
                  <h4 class="pdm-fb__card-title">${esc(n.name)}</h4>
                  <p class="pdm-fb__note-short">${esc(n.short || s.pending)}</p>
                  <div class="pdm-fb__card-actions">
                    <button type="button" class="pdm-fb__select" data-toggle-note="${esc(n.id)}" aria-pressed="${on}">
                      <span class="pdm-fb__select-text">${esc(on ? s.added : s.add)}</span><span class="visually-hidden"> ${esc(n.name)}</span>
                    </button>
                    <button type="button" class="pdm-fb__link" data-detail-note="${esc(n.id)}" aria-haspopup="dialog">
                      ${esc(s.details)}<span class="visually-hidden"> for ${esc(n.name)}</span>
                    </button>
                  </div>
                </div>
              </li>`;
          }).join('')}
        </ul>`;
    }

    /* ---------- update ---------- */

    update({ focus = false } = {}) {
      const s = this.config.strings;
      const step = this.state.step;
      const bases = this.orderedBases();
      const notes = this.orderedNotes();

      // Steps nav
      this.qa('.pdm-fb__step').forEach((btn) => {
        const current = btn.dataset.goto === step;
        if (current) btn.setAttribute('aria-current', 'step'); else btn.removeAttribute('aria-current');
        const done = (btn.dataset.goto === 'base' && bases.length) || (btn.dataset.goto === 'notes' && notes.length);
        btn.classList.toggle('is-done', Boolean(done) && !current);
      });

      // Panels
      this.qa('[data-panel]').forEach((panel) => {
        const show = panel.dataset.panel === step;
        if (show && panel.hidden) {
          panel.hidden = false;
          if (!reducedMotion()) {
            panel.classList.remove('is-entering');
            void panel.offsetWidth;
            panel.classList.add('is-entering');
          }
        } else if (!show) {
          panel.hidden = true;
        }
      });

      // Base cards
      this.qa('[data-base-card]').forEach((card) => {
        const on = this.state.bases.includes(card.dataset.baseCard);
        card.classList.toggle('is-selected', on);
        const btn = card.querySelector('[data-toggle-base]');
        btn.setAttribute('aria-pressed', String(on));
        const name = this.baseMap.get(card.dataset.baseCard).name;
        btn.querySelector('.pdm-fb__select-text').textContent = on
          ? (bases.length === 2 ? `${s.selected} · 50%` : s.selected)
          : (bases.length === 1 ? s.blendWith : s.select);
        btn.setAttribute('aria-label', on ? `Remove ${name}` : (bases.length === 1 ? `Blend ${name} 50/50 with ${bases[0].name}` : `Select ${name}`));
      });
      this.q('[data-base-counter]').textContent = bases.length === 0 ? s.baseCounter0
        : bases.length === 1 ? `${bases[0].name} · ${s.baseCounter1}` : `${bases[0].name} + ${bases[1].name} · ${s.baseCounter2}`;

      // Families & notes
      this.qa('[data-family]').forEach((btn) => btn.setAttribute('aria-pressed', String(btn.dataset.family === this.state.family)));
      this.config.families.forEach((f) => {
        const count = f.notes.filter((n) => this.state.notes.includes(n.id)).length;
        const el = this.q(`[data-family-count="${f.id}"]`);
        if (el) el.textContent = count ? `${count} chosen` : '';
      });
      // Re-render the note grid, keeping keyboard focus on the same control.
      const active = document.activeElement;
      const panel = this.q('[data-family-panel]');
      let refocus = null;
      if (active && panel.contains(active)) {
        if (active.dataset.toggleNote) refocus = `[data-toggle-note="${active.dataset.toggleNote}"]`;
        else if (active.dataset.detailNote) refocus = `[data-detail-note="${active.dataset.detailNote}"]`;
      }
      panel.innerHTML = this.familyPanelHTML();
      if (refocus && !this.replaceRequest) {
        const el = panel.querySelector(refocus);
        if (el) el.focus({ preventScroll: true });
      }
      this.q('[data-note-counter]').innerHTML = `<span class="pdm-fb__count">${notes.length}</span> of ${this.limits.notes} notes chosen${notes.length === 0 ? ` · <span class="pdm-fb__muted">${esc(s.notesOptional)}</span>` : ''}`;
      this.q('[data-note-chips]').innerHTML = notes.map((n) => `
        <li><button type="button" class="pdm-fb__chip" data-remove-note="${esc(n.id)}" aria-label="Remove ${esc(n.name)}">
          ${esc(n.name)} <svg viewBox="0 0 24 24" width="12" height="12" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="1.5" fill="none"/></svg>
        </button></li>`).join('');

      // Review (only re-render when not mid-purchase so status survives)
      if (!this.busy) {
        const statusBefore = this.q('[data-status]') ? this.q('[data-status]').innerHTML : '';
        this.q('[data-review]').innerHTML = this.reviewHTML();
        if (statusBefore && this.state.lastAdded === this.signature()) this.q('[data-status]').innerHTML = statusBefore;
      }

      // Summary + next buttons
      this.q('[data-summary]').innerHTML = this.summaryHTML();
      if (this.sheet.open) this.q('[data-sheet-summary]').innerHTML = this.summaryHTML(true);
      const nextLabel = step === 'base' ? s.nextToNotes : step === 'notes' ? (notes.length ? s.nextToReview : s.nextToReviewNoNotes) : '';
      this.qa('[data-next]').forEach((btn) => {
        btn.textContent = nextLabel;
        btn.hidden = step === 'review';
      });
      this.root.classList.toggle('is-review', step === 'review');
      this.q('[data-bar-line]').textContent = bases.length
        ? `${bases.map((b) => b.name).join(' + ')} · ${notes.length}/${this.limits.notes} notes`
        : s.noBaseYet;

      this.syncName();
      this.persist();

      if (focus) {
        const heading = this.q(`[data-panel="${step}"] .pdm-fb__h2`);
        if (heading) heading.focus({ preventScroll: true });
      }
    }

    /* ---------- cart ---------- */

    setStatus(html, kind = '') {
      const el = this.q('[data-status]');
      if (!el) return;
      el.className = `pdm-fb__status ${kind ? `pdm-fb__status--${kind}` : ''}`;
      el.innerHTML = html;
    }

    setBusy(which) {
      this.busy = Boolean(which);
      this.qa('[data-add-to-cart], [data-checkout]').forEach((btn) => {
        const active = (which === 'cart' && btn.hasAttribute('data-add-to-cart')) || (which === 'checkout' && btn.hasAttribute('data-checkout'));
        btn.setAttribute('aria-busy', String(active));
        btn.classList.toggle('is-loading', active);
        if (which) btn.setAttribute('aria-disabled', 'true');
        else if (this.config.product.variantId && this.config.product.available) btn.removeAttribute('aria-disabled');
      });
    }

    /** Section ids of the theme's cart line lists (drawer + cart page), re-rendered in the add response. */
    cartSectionIds() {
      return Array.from(document.querySelectorAll('cart-items-component'))
        .map((el) => el.dataset.sectionId)
        .filter(Boolean);
    }

    async fetchCart() {
      const res = await fetch(this.config.routes.cartJs, { headers: { Accept: 'application/json' } });
      if (!res.ok) throw new Error(this.config.strings.genericError);
      return res.json();
    }

    async cartContains(signature) {
      try {
        const cart = await this.fetchCart();
        return cart.items.some((item) => item.properties && item.properties._config === signature);
      } catch (e) {
        return false;
      }
    }

    async loadEvents() {
      // Shopify's standard storefront events (mapped in Atelier's import map).
      // Atelier's cart drawer, cart count, and cart line list all listen for these.
      if (this.events !== undefined) return this.events;
      try {
        this.events = await import('@shopify/events');
      } catch (e) {
        this.events = null;
      }
      return this.events;
    }

    async purchase({ checkout }) {
      const s = this.config.strings;
      const p = this.config.product;
      if (this.busy) return; // repeated clicks while a request is in flight are ignored
      if (!p.variantId || !p.available) {
        this.setStatus(esc(p.variantId ? s.soldOut : s.notConfigured), 'error');
        return;
      }
      if (!this.state.bases.length) {
        this.setStatus(`${esc(s.needBase)} <button type="button" class="pdm-fb__link" data-goto="base">${esc(s.chooseBase)}</button>`, 'error');
        return;
      }

      const signature = this.signature();
      this.setBusy(checkout ? 'checkout' : 'cart');
      this.setStatus(`<span class="pdm-fb__spinner" aria-hidden="true"></span> ${esc(checkout ? s.preparingCheckout : s.adding)}`);

      let deferred = null;
      let events = null;

      try {
        // Checkout after an earlier Add to cart of this same composition: don't add it twice.
        if (checkout && this.state.lastAdded === signature && await this.cartContains(signature)) {
          this.goToCheckout();
          return;
        }

        const sections = this.cartSectionIds();
        const body = { items: [{ id: Number(p.variantId), quantity: 1, properties: this.lineItemProperties() }] };
        if (!checkout && sections.length) {
          body.sections = sections.join(',');
          body.sections_url = window.location.pathname;
        }

        if (!checkout) {
          events = await this.loadEvents();
          if (events && events.CartLinesUpdateEvent) {
            deferred = events.CartLinesUpdateEvent.createPromise();
            this.root.dispatchEvent(new events.CartLinesUpdateEvent({
              action: 'add',
              context: 'product',
              lines: [{ merchandiseId: String(p.variantId), quantity: 1 }],
              promise: deferred.promise
            }));
          }
        }

        let res;
        try {
          res = await fetch(this.config.routes.cartAdd, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body: JSON.stringify(body)
          });
        } catch (networkError) {
          throw Object.assign(new Error(s.networkError), { network: true });
        }
        let data = null;
        try { data = await res.json(); } catch (e) { data = null; }

        if (!res.ok || !data || data.status) {
          const message = (data && (data.description || data.message)) || s.genericError;
          throw Object.assign(new Error(typeof message === 'string' ? message : s.genericError), { ajax: data });
        }

        this.state.lastAdded = signature;
        this.persist();

        if (checkout) {
          this.goToCheckout();
          return;
        }

        // Let the theme refresh its cart drawer and count from the same response.
        if (deferred) {
          try {
            const cart = await this.fetchCart();
            deferred.resolve({
              cart: events.CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
              detail: {
                items: cart.items,
                source: 'fragrance-builder',
                sourceId: this.config.sectionId,
                itemCount: 1,
                productId: String(p.id || ''),
                sections: data.sections,
                didError: false
              }
            });
          } catch (e) {
            deferred.reject(e);
          }
        }

        this.setBusy(false);
        this.setStatus(`
          <p class="pdm-fb__confirm-title"><svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7.5" stroke="currentColor" stroke-width="1.4" fill="none"/></svg> ${esc(s.addedConfirm)}</p>
          <p class="pdm-fb__confirm-detail">${this.finalName() ? `“${esc(this.finalName())}” · ` : ''}${esc(this.baseLabel())} · ${esc(this.notesLabel())}</p>
          <div class="pdm-fb__confirm-actions">
            <a href="${esc(this.config.routes.cart)}" class="pdm-fb__link" data-view-cart>${esc(s.viewCart)}</a>
          </div>`, 'success');
        this.announce(s.addedConfirm);
      } catch (error) {
        if (deferred && events) {
          if (events.CartErrorEvent) {
            this.root.dispatchEvent(new events.CartErrorEvent({
              error: error.message || s.genericError,
              code: error.network ? 'SERVICE_UNAVAILABLE' : 'INVALID'
            }));
          }
          try {
            const cart = await this.fetchCart();
            deferred.resolve({
              cart: events.CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
              detail: { didError: true, items: cart.items, source: 'fragrance-builder', sourceId: this.config.sectionId, itemCount: 1 }
            });
          } catch (e) {
            deferred.reject(e);
          }
        }
        this.setBusy(false);
        this.setStatus(`${esc(error.message || s.genericError)} <span class="pdm-fb__muted">${esc(s.tryAgain)}</span>`, 'error');
        this.announce(error.message || s.genericError);
      }
    }

    goToCheckout() {
      this.setStatus(`<span class="pdm-fb__spinner" aria-hidden="true"></span> ${esc(this.config.strings.toCheckout)}`);
      window.location.assign(this.config.routes.checkout);
      // If the page is still here (slow network, or restored with Back), unlock the buttons.
      // A repeat Checkout of the same composition won't add it again (see purchase()).
      window.clearTimeout(this.unlockTimer);
      this.unlockTimer = window.setTimeout(() => this.unlock(), 6000);
    }

    unlock() {
      if (!this.busy) return;
      this.setBusy(false);
      this.setStatus('');
    }

    cartDrawer() {
      const inner = document.querySelector('cart-drawer-component');
      return (inner && inner.closest('theme-drawer')) || document.querySelector('theme-drawer#cart-drawer');
    }

    viewCart(event) {
      const drawer = this.cartDrawer();
      if (drawer && typeof drawer.open === 'function') {
        event.preventDefault();
        this.lastTrigger = event.target.closest('a, button');
        drawer.open();
      }
    }
  }

  const init = (scope = document) => {
    scope.querySelectorAll('[data-fragrance-builder]').forEach((root) => {
      if (root.__pdmFb) return;
      root.__pdmFb = new FragranceBuilder(root);
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();

  // Re-initialise when the section is added or re-rendered in the theme editor.
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
